<template>
  <BaseModal ref="modal">
    <UiModalDialog class="jps-tag-editor">
      <template #header>
        <BaseIcon size="size-6" name="mdi/tag" />
        {{ modalTitle }}
      </template>
      <div class="jps-modal-content" v-if="tempTag">
        <form @submit.prevent="ok()">
          <div class="jps-field">
            <label class="jps-label">タグの種別</label>
            <BaseRadioSelect :enum="type" v-model="tempTag.type" />
          </div>

          <div v-if="tempTag.type === 'WORD'">
            <ValidationForm
              :target="tempTag"
              :rule="idRule"
              @valid="idValid = $event"
            />
          </div>
          <div v-if="tempTag.type === 'COLOR'">
            <div class="jps-field">
              <label class="jps-label" style="width: fit-content">
                色
                <input type="color" v-model="color" />
              </label>
            </div>
          </div>
          <div v-if="tempTag.type === 'TIME'"></div>
          <div v-if="tempTag.type === 'LOCATION'"></div>
          <ValidationForm
            :target="tempTag"
            :rule="rule"
            @valid="valid = $event"
            except="id,type,equivalence"
          />
        </form>
      </div>
      <template #buttons>
        <button @click="modal.reject()">{{ ls(dapLangGlobal.cancel) }}</button>
        <button @click="ok()" :disabled="!allValid">{{ ls({ ja: '作成', en: 'Create' }) }}</button>
      </template>
    </UiModalDialog>
  </BaseModal>
</template>

<script lang="ts" setup>
import { dapLangGlobal } from '#imports'
import type { BaseModal } from '#components'

const { ls, lst } = useI18n()
const { loadingExecute } = useLoader()
const { snackbarError } = useSnackbar()
const { createTag } = useTag()

interface Props {
  label?: string,
  tag?: Tag
}
const props = withDefaults(defineProps<Props>(),{
})

const modal = ref<InstanceType<typeof BaseModal>>()
const _label: Ref<string> = ref()
const _tag: Ref<Tag> = ref()
const tempTag: Ref<Tag> = ref(null)
const idValid: Ref<boolean> = ref(false)
const valid: Ref<boolean> = ref(false)
const color: Ref<any> = ref('#000000')
const openAwait = async (options?: Props) => {
  tempTag.value = null
  idValid.value = false
  valid.value = false
  color.value = '#000000'

  setOptionsValue(_label, 'label', props, options)
  setOptionsValue(_tag, 'tag', props, options)

  if (_tag.value) {
  } else {
    tempTag.value = {
      label: { ja: _label.value || '', en: '' },
      id: '',
      type: 'WORD',
      description: { ja: '', en: '' },
      equivalence: [],
    }
  }

  return await modal.value.openAwait<Tag>()
}
defineExpose({ openAwait })

const TAG_TYPE_DEF_LIMITED: EnumDef = Object.freeze({
  id: 'jps-tag_type',
  c: {
    WORD: {
      l: {
        ja: 'キーワード',
        en: 'Keyword',
      },
      d: {},
    },
    COLOR: {
      l: {
        ja: '色',
        en: 'Color',
      },
      d: {},
    },
  },
})

const type: EnumDef = TAG_TYPE_DEF_LIMITED
const rule: ValidationRule = TAG_RULE
const idRule: ValidationRule = {
  fields: {
    id: {
      field: 'id',
      label: { ja: 'ID' },
      description: {
        ja: '任意のIDを入れて下さい。半角英数字、_（アンダーバー）のみ使えます',
      },
      type: 'string',
      mandatory: true,
      inputHelp: { ja: '半角英数字と_（アンダーバー）のみ使えます' },
      strPattern: '[A-z0-9_]+',
    },
  },
}

const modalTitle = computed(() => {
  if (_tag.value == null) {
    return ls({ ja: '新しいタグの作成', en: 'Create New Tag' })
  } else {
    return lst(
      { ja: '{}の編集', en: 'Edit {}' },
      ls(_tag.value.label)
    )
  }
})

const allValid = computed(() => {
  switch (tempTag.value.type) {
    case 'WORD':
      return valid.value && idValid.value
    case 'COLOR':
      return valid.value
  }
  return false
})

const ok = async () => {
  if (allValid.value) {
    let upTag = clone(tempTag.value)
    switch (tempTag.value.type) {
      case 'WORD':
        upTag.id = 'w-' + tempTag.value.id
        break
      case 'COLOR':
        upTag.id = 'c-' + color.value.slice(1)
        break
    }
    try {
      const updated = await loadingExecute(createTag(upTag))
      if (!updated) return
      modal.value.resolve(updated)
    } catch (error) {
      snackbarError({ ja: '失敗しました…' })
    }
  }
}
</script>

<style lang="scss" scoped>
</style>
